import { DayPicker, DateRange, OnSelectHandler } from 'react-day-picker';
import { cva } from 'class-variance-authority';

import { cn } from 'styles';

import { ChevronLeftIcon, ChevronRightIcon } from '../Icons';
import { calculateFromToRange } from './utils';
import { getLastItem } from 'utils/array';

export type { DateRange };

/*
  To do:

  - month/year navigation via caption layout + style
  - easily way to expand it to show it like an "event" calendar?
*/

type BaseCalendarProps = React.ComponentProps<typeof DayPicker>;

type AllowedProps = Pick<
  BaseCalendarProps,
  | 'className'
  | 'classNames'
  | 'showOutsideDays'
  | 'month'
  | 'onMonthChange'
  | 'autoFocus'
  | 'disableNavigation'
  | 'disabled'
> & {
  fromDate?: Date;
  toDate?: Date;

  /**
   * Enables the user to unselect completely,
   * by default this is `true`, meaning the user
   * can't unselect
   */
  required?: boolean;
};

type SelectionProps =
  | {
      mode: 'single';
      /** The selected day. */
      selected?: Date | undefined;
      /** Event fired when a day is selected. */
      onSelect?: OnSelectHandler<Date | undefined>;

      min?: never;
      max?: never;
    }
  | {
      mode: 'range';
      /** The selected range of days. */
      selected?: DateRange | undefined;
      /** Event fired when a range (or a part of the range) is selected. */
      onSelect?: OnSelectHandler<DateRange>;
      /** The minimum amount of days that can be selected. */
      min?: number;
      /** The maximum amount of days that can be selected. */
      max?: number;
    }
  | {
      mode: 'multiple';
      /** The selected days. */
      selected?: Date[] | undefined;
      /** Event fired when a days added or removed to the selection. */
      onSelect?: OnSelectHandler<Date[] | undefined>;
      /** The minimum amount of days that can be selected. */
      min?: number;
      /** The maximum amount of days that can be selected. */
      max?: number;
    };

export type DayPickerMode = SelectionProps['mode'];

function getDefaultMonth(props: SelectionProps) {
  if (props.mode === 'single') return props.selected || undefined;

  if (props.mode === 'range') return props.selected?.to;

  return getLastItem(props.selected ?? []);
}

export type CalendarProps<Mode extends DayPickerMode> = AllowedProps &
  Extract<SelectionProps, { mode: Mode }> & {
    numberOfMonths?: 1 | 2;

    disableFuture?: boolean;
    disablePast?: boolean;
    required?: boolean;
  };

const buttonVariants = cva(
  [
    'inline-flex items-center justify-center whitespace-nowrap text-sm font-medium transition-colors',
    'ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
    'disabled:pointer-events-none disabled:opacity-50',
  ].join(' '),
  {
    variants: {
      variant: {
        outline: 'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
      },
    },
  },
);

const classesThatWorkInDrawer = {
  month: 'phone:w-full',
  rows: 'phone:grid phone:grid-cols-7 phone:justify-items-center',
};

export function Calendar<Mode extends DayPickerMode>({
  className,
  classNames,
  disableFuture,
  toDate,
  fromDate,
  disablePast,
  disabled,
  ...props
}: CalendarProps<Mode>) {
  return (
    <DayPicker
      required
      {...props}
      {...calculateFromToRange({ disableFuture, disablePast, fromDate, toDate, disabled })}
      // workaround as it would always open on the current month
      defaultMonth={getDefaultMonth(props)}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      selected={(props.selected as any) || undefined}
      className={cn('p-3', className)}
      classNames={{
        months: 'flex relative',
        month: `${classesThatWorkInDrawer.month}`,
        month_caption: 'flex justify-center pt-1 items-center',
        caption_label: 'text-sm font-medium',

        nav: 'space-x-1 flex items-center absolute inset-x-0 top-0',
        button_previous: cn(
          buttonVariants({ variant: 'outline' }),
          'absolute left-1 top-0 h-7 w-7 rounded-md bg-transparent p-0 opacity-50 hover:opacity-100 disabled:opacity-20',
        ),
        button_next: cn(
          buttonVariants({ variant: 'outline' }),
          'absolute right-1 top-0 h-7 w-7 rounded-md bg-transparent p-0 opacity-50 hover:opacity-100 disabled:opacity-20',
        ),

        month_grid: 'w-full border-collapse space-y-1 mt-4 phone:mt-6',

        weekday: 'text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]',
        weekdays: `flex ${classesThatWorkInDrawer.rows}`,
        week: `flex w-full mt-2 ${classesThatWorkInDrawer.rows}`,

        range_end: 'rounded-r-md',
        range_start: 'rounded-l-md',
        range_middle: 'aria-selected:bg-accent aria-selected:text-accent-foreground',

        day: cn(
          buttonVariants({ variant: 'ghost' }),
          'relative h-9 w-9 p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-accent [&:has([aria-selected].day-outside)]:bg-accent/50',
          props.mode === 'range' ? '' : 'rounded-md',
        ),
        day_button: cn('h-9 w-9 select-none p-0 font-normal aria-selected:opacity-100'),
        selected:
          'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground',
        today: 'bg-accent text-accent-foreground rounded-md',
        outside:
          'day-outside text-muted-foreground opacity-50 bg-red-300 aria-selected:bg-accent/50 aria-selected:text-muted-foreground aria-selected:opacity-30',
        disabled: 'text-muted-foreground opacity-50',
        hidden: 'invisible',
        ...classNames,
      }}
      components={{
        Chevron: (props) =>
          props.orientation === 'left' ? (
            <ChevronLeftIcon {...props} size={16} />
          ) : (
            <ChevronRightIcon {...props} size={16} />
          ),
      }}
    />
  );
}
